import { useState } from 'react';
import { ChevronRight, Mail, MapPin, Phone, Send, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully. We will be in touch shortly.');
      setFormData({ name: '', email: '', company: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="hero-section py-16 md:py-20">
        <div className="container-law">
          <div className="breadcrumb text-white/60 mb-6">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <span>Contact</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-white mb-4">Contact Us</h1>
          <p className="text-white/80 text-lg max-w-2xl">
            Whether you have a specific transaction or want to explore how we might work together, we're happy to have an initial conversation.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 md:py-20">
        <div className="container-law">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-28 space-y-8">
                <div>
                  <h3 className="font-heading text-xl text-navy mb-6">Reach Out Directly</h3>
                  <div className="space-y-6">
                    <a
                      href="mailto:info@kattorney.com"
                      className="flex items-start gap-4 group"
                      data-testid="contact-email"
                    >
                      <div className="w-10 h-10 bg-green/10 flex items-center justify-center group-hover:bg-green transition-colors">
                        <Mail className="w-5 h-5 text-green group-hover:text-white transition-colors" strokeWidth={1.5} />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Email</p>
                        <p className="text-navy font-medium group-hover:text-green transition-colors">info@kattorney.com</p>
                      </div>
                    </a>

                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-green/10 flex items-center justify-center">
                        <Phone className="w-5 h-5 text-green" strokeWidth={1.5} />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Phone</p>
                        <p className="text-navy font-medium">By appointment</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-green/10 flex items-center justify-center">
                        <MapPin className="w-5 h-5 text-green" strokeWidth={1.5} />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Location</p>
                        <p className="text-navy font-medium">2337 Philmont Avenue, Suite 205</p>
                        <p className="text-gray-600 text-sm">Huntingdon Valley, PA 19006</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-8">
                  <h4 className="font-medium text-navy mb-2">Response Time</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    We typically respond within one business day. For urgent matters, please indicate in your message.
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-gray-50 border border-gray-200 p-8 md:p-10">
                <h3 className="font-heading text-2xl text-navy mb-6">Send Us a Message</h3>
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="input-field"
                        placeholder="Your name"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="input-field"
                        placeholder="your@email.com"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                        Company
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="input-field"
                        placeholder="Your company"
                        data-testid="contact-company-input"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="input-field"
                        placeholder="Your phone number"
                        data-testid="contact-phone-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                      Subject *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="input-field"
                      placeholder="Brief description of your inquiry"
                      data-testid="contact-subject-input"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="input-field resize-none"
                      placeholder="Tell us about your matter or how we can help"
                      data-testid="contact-message-input"
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-navy disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="contact-submit-btn"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                      <Send className="w-4 h-4 ml-2" strokeWidth={2} />
                    </button>
                    <p className="text-xs text-gray-500">
                      This form is for general inquiries. Submitting does not create an attorney-client relationship.
                    </p>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Alternative CTA */}
      <section className="py-12 bg-gray-50 border-t border-gray-200">
        <div className="container-law text-center">
          <h3 className="font-heading text-xl text-navy mb-2">Prefer to schedule a call directly?</h3>
          <p className="text-gray-600 mb-6">Reach out via email to schedule a brief introductory conversation.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="mailto:info@kattorney.com?subject=Scheduling%20Request"
              className="btn-outline inline-flex items-center"
              data-testid="schedule-call-btn"
            >
              Schedule via Email <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
            </a>
            <button
              onClick={() => {
                navigator.clipboard.writeText('info@kattorney.com');
                toast.success('Email address copied to clipboard');
              }}
              className="text-gray-500 hover:text-navy text-sm underline"
              data-testid="copy-email-btn"
            >
              Or copy email address
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};
